import { Ranges } from '../ranges.js';
import { WireTypes } from '../wireTypes.js';
import { CommitAnalyzer } from './commitAnalyzer.js';
import { Stats as S } from './stats.js';
import { TestOutcomes as T } from './testOutcomes.js';

export type StatsAnnotationProfile = {
  annotationIdx: S.AnnotationIndex,
  tests: T.TestRanges,
}[];

export class StatsAnalyzer {
  private _testLineNumbers: Int32Array;

  private _etag: string;
  
  private _errs: WireTypes.TestError[] = [];
  private _annotationProfiles: StatsAnnotationProfile[] = [];
  private _annotations: WireTypes.Annotation[] = [];

  private _commitIdToJSONCommit = new Map<S.CommitId, S.JSONCommit>();

  // Serves the constructors only: this one and those of the commit analyzers.
  // The same ranges repeat across the runs of a shard, so each is decoded once.
  private _rangesCache = new Map<Ranges.CompressedRanges<S.TestIndex>, T.TestRanges>();

  // A commit analyzer does not change once it is created, and it carries the
  // caches of everything computed for its commit. So every snapshot that reads
  // this shard gets the same analyzers, instead of starting from empty caches.
  private _commitAnalyzers = new Map<S.CommitId, CommitAnalyzer>();

  private _epoch: S.TestsEpoch;

  /** `stale` is the previous version of this shard, to take the analyzers of unchanged commits from. */
  constructor(jsonStats: S.JSONData, etag?: string, stale?: StatsAnalyzer) {
    // If the data version is incompatible with this code version, or if the test index
    // has been changed, then we have to fall-back to empty filter result.
    if (jsonStats.version < S.STATS_VERSION) {
      jsonStats = S.createEmptyStats('<empty>' as S.TestsEpoch);
      etag = S.computeDataSignature(jsonStats);
    } else {
      etag = etag ?? S.computeDataSignature(jsonStats);
    }
    this._etag = etag;
    this._epoch = jsonStats.epoch;

    // Convert once per shard and hand the same array to every CommitAnalyzer.
    // The Int32Array conversion maps null/undefined to 0, which is the
    // normalization we want for missing line numbers.
    this._testLineNumbers = new Int32Array(jsonStats.testLineNumbers);

    for (let i = 0 as S.ErrorIndex; i < jsonStats.errors.length; ++i) {
      const error: WireTypes.TestError = {
        errorId: S.computeErrorId(jsonStats.errors[i]),
        message: jsonStats.errors[i].message,
        value: jsonStats.errors[i].value,
      };
      this._errs.push(error);
    }

    for (let i = 0 as S.AnnotationIndex; i < jsonStats.annotations.length; ++i) {
      const annotation: WireTypes.Annotation = {
        annotationId: S.computeAnnotationId(jsonStats.annotations[i]),
        type: jsonStats.annotations[i].type,
        description: jsonStats.annotations[i].description,
      };
      this._annotations.push(annotation);
    }

    for (let commitIndex = 0 as S.CommitIndex; commitIndex < jsonStats.commits.length; ++commitIndex) {
      const commit = jsonStats.commits[commitIndex];
      this._commitIdToJSONCommit.set(commit.commitId, commit);
    }

    this._annotationProfiles = jsonStats.annotationProfiles.map(profile => profile.map(({ annotationIdx, tests }) => ({
      annotationIdx,
      tests: this._decompress(tests),
    })))

    // Most commits of a shard are the same in two versions of it. Their
    // analyzers move over, together with everything they have computed.
    if (stale && stale._epoch === this._epoch) {
      for (const [commitId, analyzer] of stale._commitAnalyzers) {
        const jsonCommit = this._commitIdToJSONCommit.get(commitId);
        const reused = jsonCommit && analyzer.tryToReuse(jsonCommit);
        if (reused)
          this._commitAnalyzers.set(commitId, reused);
      }
    }
  }

  etag() {
    return this._etag;
  }

  epoch() {
    return this._epoch;
  }

  private _decompress(compressed: Ranges.CompressedRanges<S.TestIndex>): T.TestRanges {
    let result = this._rangesCache.get(compressed);
    if (!result) {
      result = Ranges.decompress(compressed);
      this._rangesCache.set(compressed, result);
    }
    return result;
  }

  getCommitAnalyzerEtag(commitId: S.CommitId) {
     return CommitAnalyzer.computeEtag(this._epoch, commitId, this._commitIdToJSONCommit.get(commitId));
  }

  /** `runs` limits the analyzer to these runs of the commit; without it, the analyzer covers all of them. */
  getCommitAnalyzer(commitId: S.CommitId, runs?: S.RunId[]) {
    const jsonCommit = this._commitIdToJSONCommit.get(commitId);
    const runIds = runs ? new Set(runs) : undefined;
    // Only the analyzers that cover the whole commit are kept. A caller asks for
    // fewer runs when the report index has dropped some that this shard still
    // has, which lasts until the shard is rebuilt.
    if (runIds && jsonCommit?.runs.some(run => !runIds.has(run.runId)))
      return this._createCommitAnalyzer(commitId, jsonCommit, runIds);
    let analyzer = this._commitAnalyzers.get(commitId);
    if (!analyzer) {
      analyzer = this._createCommitAnalyzer(commitId, jsonCommit, undefined);
      this._commitAnalyzers.set(commitId, analyzer);
    }
    return analyzer;
  }

  private _createCommitAnalyzer(commitId: S.CommitId, jsonCommit: S.JSONCommit|undefined, runIds: Set<S.RunId>|undefined) {
    return new CommitAnalyzer(
      commitId,
      this._epoch,
      this._errs,
      this._annotationProfiles,
      this._annotations,
      this._testLineNumbers,
      this._decompress.bind(this),
      jsonCommit,
      runIds,
    );
  }
}
